import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import commaNumber from 'comma-number';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import Sorter from '@/components/DataTable/Sorter';
import List from '@/components/List/List';
import Typo from '@/components/Typography/Typo';
import queryKeys from '@/configs/react-query-keys';
import AskSynclyPanel from '@/features/AskSyncly/components/AskSynclyPanel';
import { FeedbackExport } from '@/features/Feedback/components/FeedbackExport/FeedbackExport';
import { FEEDBACK_SORT_OPTIONS } from '@/features/Feedback/configs/feedback-sort-options';
import { useFeedbackSortState } from '@/features/Feedback/hooks/useFeedbackSortState';
import FeedbackCard from '@/features/Taxonomy/components/FeedbackCard/FeedbackCard';
import useTrackModal from '@/hooks/domain/useTrackModal';
import useKeymap from '@/hooks/pure/useKeymap';
import service from '@/services/index';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
  type DetailModalOptions,
} from '@/stores/pure/DetailModalStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { detailModalStyle } from '@/styles/style';
import { GPT_DOMAIN } from '@/types/gptConst';

type Props = {
  alias: string;
  pos: number;
  options: DetailModalOptions<GlobalSearchSummaryFeedbackListModalOptions>;
  closeModal: () => void;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
};

const GlobalSearchSummaryFeedbackListModal = (props: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const chatMap = useHeySynclyStore((state) => state.chatMap);
  const leaveChat = useHeySynclyStore((state) => state.leaveChat);
  const setPopup = usePopup();
  const [sortStatus, setSortStatus] = useFeedbackSortState();
  const [chatAlias, setChatAlias] = useState<string | null>(null);
  const chat = chatAlias ? chatMap.get(chatAlias) : undefined;
  const [selectedFeedback, setSelectedFeedback] =
    useState<SelectedFeedbackState>(null);

  const { data, isLoading } = useQuery({
    queryKey: [
      queryKeys.search.SEARCH_SUMMARY_ISSUE_FEEDBACK_LIST,
      props.alias,
      props.options.issueIndex,
      props.options.subIssueIndex,
      sortStatus?.sortBy,
      sortStatus?.orderStatus,
    ],
    queryFn: () =>
      service.globalSearch.fetchSummaryIssueFeedbackList(
        props.alias,
        props.options.issueIndex,
        props.options.subIssueIndex,
        sortStatus,
      ),
  });

  const onFeedbackItemClick = (state: SelectedFeedbackState) => {
    if (!state) return;
    if (selectedFeedback?.feedbackAlias === state.feedbackAlias) {
      if (selectedFeedback?.blockAlias === state.blockAlias) {
        setSelectedFeedback(null);
      } else {
        setSelectedFeedback(state);
        setNewModal({
          alias: state.feedbackAlias,
          type: DETAIL_MODAL_TYPE.FEEDBACK,
          idx: 1,
          options: {
            focusedBlock: state.blockAlias,
          },
        });
      }
    } else {
      setSelectedFeedback(state);
      setNewModal({
        alias: state.feedbackAlias,
        type: DETAIL_MODAL_TYPE.FEEDBACK,
        idx: 1,
        options: {
          focusedBlock: state.blockAlias,
        },
      });
    }
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: 0,
    });
  }, [props.alias]);

  useTrackModal({
    domain: 'trending',
    alias: props.alias,
  });

  const onCloseClick = () => {
    if (chatAlias && chat?.alias === chatAlias) {
      setPopup({
        type: 'confirm',
        popupTheme: 'negative',
        title: 'Would you like to close this window?',
        message: 'If you close the window, the content will be deleted.',
        confirmText: 'Okay',
        onConfirm: () => {
          leaveChat(chatAlias);
          props.closeModal();
        },
      });
    } else {
      props.closeModal();
    }
  };

  useKeymap('Escape', onCloseClick);

  const onExportFeedbackListClick = async (
    reason?: string,
    password?: string,
  ) => {
    const search_alias = props.alias;
    const issue_index = props.options.issueIndex;
    const subissue_index = props.options.subIssueIndex;
    if (typeof subissue_index === 'undefined') {
      await service.globalSearch.exportSummaryIssueFeedbackList({
        search_alias,
        issue_index,
        password,
        reason,
      });
    } else {
      await service.globalSearch.exportSummarySubIssueFeedbackList({
        search_alias,
        issue_index,
        subissue_index,
        password,
        reason,
      });
    }
  };

  return (
    <ModalStyle status={true} pos={props.pos} ref={modalRef}>
      <Wrapper f={1}>
        <Flex j="flex-end" a="center" style={{ padding: '16px 32px' }}>
          <Flex g="6px">
            <FeedbackExport onExportClick={onExportFeedbackListClick}>
              <Button leftIcon={<DownloadOutlined />} variant="outlined">
                Export to CSV
              </Button>
            </FeedbackExport>
            <IconButton
              icon={<CloseOutlined />}
              onClick={onCloseClick}
              tooltip="Close"
              shortcut="ESC"
            />
          </Flex>
        </Flex>
        <Divider />
        <Space h={24} />
        <VFlex padding="0px 32px" g={12}>
          <Flex g={4}>
            <Typo type="label1" className="text-gray-600">
              {props.options.title}
            </Typo>
          </Flex>
        </VFlex>
        <Space h={24} />
        <div className="px-8 flex justify-between items-center">
          <Typo type="label2">
            Total feedback : {commaNumber(data?.count || 0)}
          </Typo>
          <Sorter
            options={FEEDBACK_SORT_OPTIONS}
            currSortStatus={sortStatus}
            onSortChange={setSortStatus}
          />
        </div>
        <Space h={24} />
        <List
          isLoading={isLoading}
          data={data?.feedbacks}
          className="gap-2 px-8 pb-50 h-full"
          render={(f) => (
            <FeedbackCard
              selectedFeedback={selectedFeedback}
              selectedUnit={null}
              setSelectedFeedback={onFeedbackItemClick}
              key={f.alias}
              feedback={f}
            />
          )}
        />
        <AskSynclyPanel
          chat={chat}
          socketInfo={{
            domain: GPT_DOMAIN.GLOBAL_SEARCH_SUMMARY,
            search_summary: {
              alias: props.alias,
              issue_index: props.options.issueIndex,
              sub_issue_index: props.options.subIssueIndex,
            },
          }}
          onSocketConnectChange={setChatAlias}
        />
      </Wrapper>
    </ModalStyle>
  );
};

const Wrapper = styled(VFlex)`
  overflow: auto;
  height: 100%;
`;

const ModalStyle = styled(FadeDiv)<{ pos: number }>`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: ${({ pos }) => pos}px;
  ${detailModalStyle};
`;

export default GlobalSearchSummaryFeedbackListModal;
